import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
}

function getComputedCanvasColor(varName: string, alpha: number): string {
  // Safer approach: read the CSS variable directly from the root element
  // instead of creating and destroying DOM elements
  try {
    const rootStyles = getComputedStyle(document.documentElement);
    const value = rootStyles.getPropertyValue(varName).trim();
    if (!value) {
      // Fallback: use a temp div only if getComputedStyle on root fails
      const temp = document.createElement('div');
      temp.style.cssText = `position:fixed;pointer-events:none;opacity:0;width:0;height:0;background-color:oklch(var(${varName}) / ${alpha})`;
      document.body.appendChild(temp);
      const computed = getComputedStyle(temp).backgroundColor;
      document.body.removeChild(temp);
      return computed;
    }
    return `oklch(${value} / ${alpha})`;
  } catch {
    return `oklch(var(${varName}) / ${alpha})`;
  }
}

function isLowPowerDevice(): boolean {
  return (
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    window.matchMedia('(prefers-reduced-data: reduce)').matches ||
    window.matchMedia('(update: slow)').matches
  );
}

function isMicroScreen(): boolean {
  return window.innerWidth < 320 || window.innerHeight < 320;
}

export default function AmbientBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animIdRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (isMicroScreen()) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let particles: Particle[] = [];
    const lowPower = isLowPowerDevice();

    const colors = [
      getComputedCanvasColor('--primary-500', lowPower ? 0.04 : 0.06),
      getComputedCanvasColor('--accent-500', lowPower ? 0.04 : 0.06),
      getComputedCanvasColor('--secondary-500', lowPower ? 0.04 : 0.06),
      getComputedCanvasColor('--primary-500', lowPower ? 0.03 : 0.04),
      getComputedCanvasColor('--accent-500', lowPower ? 0.03 : 0.04),
    ];

    function resize() {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    }

    function initParticles() {
      if (!canvas) return;
      const divisor = lowPower ? 80000 : window.innerWidth < 480 ? 50000 : 28000;
      const count = Math.floor((canvas.width * canvas.height) / divisor);
      particles = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.3,
          vy: (Math.random() - 0.5) * 0.3,
          size: Math.random() * 120 + 40,
          opacity: Math.random() * 0.25 + 0.05,
          color: colors[Math.floor(Math.random() * colors.length)],
        });
      }
    }

    function draw() {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x < -200) p.x = canvas.width + 200;
        if (p.x > canvas.width + 200) p.x = -200;
        if (p.y < -200) p.y = canvas.height + 200;
        if (p.y > canvas.height + 200) p.y = -200;

        const gradient = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size);
        gradient.addColorStop(0, p.color);
        gradient.addColorStop(1, 'transparent');

        ctx.beginPath();
        ctx.fillStyle = gradient;
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }

      animIdRef.current = requestAnimationFrame(draw);
    }

    resize();
    initParticles();
    draw();

    let resizeTimer: ReturnType<typeof setTimeout>;
    function handleResize() {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resize();
        initParticles();
      }, 200);
    }

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      cancelAnimationFrame(animIdRef.current);
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimer);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-0"
      aria-hidden="true"
    />
  );
}